import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Badge } from 'flowbite-react';
import { Change } from '../../models';
import { Icon, UserAvatar } from '..';
import {
    handleChangeMiddleClick,
    preventMiddleClickAutoscroll,
} from '../../utils/openChangeInNewBrowserTab';
import { requesterEmailsOf, requesterOf } from '../../utils/changeRequester';
import {
    DEFAULT_VISIBLE_COLUMNS,
    loadVisibleColumns,
    OVERVIEW_COLUMN_IDS,
    saveVisibleColumns,
    type OverviewColumnId,
    type SortDirection,
} from './columns';

export interface ChangesOverviewTableProps {
    changes: Change[];
    selectedChangeIds: string[];
    sortBy: OverviewColumnId;
    sortDir: SortDirection;
    onSortChange: (sortBy: OverviewColumnId, sortDir: SortDirection) => void;
    onToggleSelect: (changeId: string) => void;
    onToggleSelectAll: () => void;
    onRowClick: (change: Change) => void;
}

function formatDate(value?: string): string {
    if (!value) return '—';
    return moment(value).format('DD/MM/YYYY');
}

function formatDateTime(value?: string): string {
    if (!value) return '—';
    return moment(value).format('DD/MM/YYYY HH:mm');
}

function latestSprintName(change: Change): string {
    const latest = change.sprints?.length ? change.sprints[change.sprints.length - 1] : null;
    return latest?.name || '—';
}

function SortIndicator({ active, dir }: { active: boolean; dir: SortDirection }) {
    if (!active) {
        return <span className="ml-1 text-gray-300 dark:text-gray-600">↕</span>;
    }
    return (
        <span className="ml-1 text-blue-600 dark:text-blue-400">
            {dir === 'asc' ? '↑' : '↓'}
        </span>
    );
}

function ColumnPicker({
    visibleColumns,
    onChange,
}: {
    visibleColumns: OverviewColumnId[];
    onChange: (next: OverviewColumnId[]) => void;
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const toggleColumn = (columnId: OverviewColumnId) => {
        if (visibleColumns.includes(columnId)) {
            if (visibleColumns.length <= 1) return;
            onChange(visibleColumns.filter((id) => id !== columnId));
            return;
        }
        onChange([...visibleColumns, columnId]);
    };

    return (
        <div className="relative" ref={rootRef}>
            <button
                type="button"
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                onClick={() => setOpen((value) => !value)}
            >
                <Icon type="list" size={4} />
                {t('changes_overview.columns.label')}
            </button>
            {open && (
                <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    <p className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                        {t('changes_overview.columns.label')}
                    </p>
                    <ul className="max-h-72 space-y-1 overflow-y-auto">
                        {OVERVIEW_COLUMN_IDS.map((columnId) => {
                            const checked = visibleColumns.includes(columnId);
                            return (
                                <li key={columnId}>
                                    <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={checked && visibleColumns.length <= 1}
                                            onChange={() => toggleColumn(columnId)}
                                            className="h-4 w-4"
                                        />
                                        {t(`changes_overview.columns.${columnId}`)}
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                    <button
                        type="button"
                        className="mt-2 w-full rounded px-2 py-1 text-left text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                        onClick={() => onChange([...DEFAULT_VISIBLE_COLUMNS])}
                    >
                        {t('changes_overview.columns.reset')}
                    </button>
                </div>
            )}
        </div>
    );
}

const ChangesOverviewTable: React.FC<ChangesOverviewTableProps> = ({
    changes,
    selectedChangeIds,
    sortBy,
    sortDir,
    onSortChange,
    onToggleSelect,
    onToggleSelectAll,
    onRowClick,
}) => {
    const { t, i18n } = useTranslation();
    const [visibleColumns, setVisibleColumns] = useState<OverviewColumnId[]>(() => loadVisibleColumns());

    const selectedCount = selectedChangeIds.length;
    const allSelected = changes.length > 0 && selectedCount === changes.length;
    const someSelected = selectedCount > 0 && !allSelected;

    const orderedVisibleColumns = useMemo(() => (
        OVERVIEW_COLUMN_IDS.filter((id) => visibleColumns.includes(id))
    ), [visibleColumns]);

    const onVisibleColumnsChange = (next: OverviewColumnId[]) => {
        setVisibleColumns(next);
        saveVisibleColumns(next);
    };

    const onHeaderClick = (columnId: OverviewColumnId) => {
        if (sortBy === columnId) {
            onSortChange(columnId, sortDir === 'asc' ? 'desc' : 'asc');
            return;
        }
        const defaultDesc: OverviewColumnId[] = ['createdAt', 'updatedAt', 'priority', 'estimate'];
        onSortChange(columnId, defaultDesc.includes(columnId) ? 'desc' : 'asc');
    };

    const renderCell = (change: Change, columnId: OverviewColumnId) => {
        switch (columnId) {
            case 'slug':
                return (
                    <Badge color={change.color()} className="w-fit whitespace-nowrap text-xs">
                        <Icon type={change.iconName()} size={3} className="inline" />
                        &nbsp;{change.slugName()}
                    </Badge>
                );
            case 'title':
                return (
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                        {change.localizedTitle(i18n.language) || '—'}
                    </span>
                );
            case 'status':
                return t(`changes.progress_status_type.${change.progress?.status}`)
                    || change.progress?.status
                    || '—';
            case 'project':
                return change.project?.name || '—';
            case 'type':
                return change.type ? (t(`projects.${change.type}`) || change.type) : '—';
            case 'assignee':
                return change.assignee?._id
                    ? (
                        <span className="inline-flex items-center gap-1.5">
                            <UserAvatar user={change.assignee} size={5} />
                            <span className="truncate">
                                {change.assignee.fullname?.() || change.assignee.email}
                            </span>
                        </span>
                    )
                    : (
                        <span className="text-gray-400">{t('priorities.filters.unassigned')}</span>
                    );
            case 'requestors': {
                const emails = requesterEmailsOf(change);
                if (!emails.length) {
                    return <span className="text-gray-400">—</span>;
                }
                return (
                    <span className="max-w-xs truncate" title={requesterOf(change)}>
                        {emails.length === 1 ? emails[0] : `${emails[0]} +${emails.length - 1}`}
                    </span>
                );
            }
            case 'estimate':
                return `${change.estimate ?? 0}${t('changes.estimate_units_min')}`;
            case 'priority':
                return change.priority ?? '—';
            case 'sprint':
                return latestSprintName(change);
            case 'createdAt':
                return formatDate(change.createdAt);
            case 'updatedAt':
                return formatDateTime(change.updatedAt);
            case 'ticket':
                return change.ticket_number || '—';
            default:
                return '—';
        }
    };

    return (
        <div>
            <div className="mb-2 flex justify-end">
                <ColumnPicker
                    visibleColumns={visibleColumns}
                    onChange={onVisibleColumnsChange}
                />
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
                        <tr>
                            <th className="w-10 px-3 py-2 text-left">
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                    onChange={onToggleSelectAll}
                                    className="h-4 w-4 cursor-pointer"
                                    aria-label={t('priorities.select_all') || ''}
                                />
                            </th>
                            {orderedVisibleColumns.map((columnId) => (
                                <th
                                    key={columnId}
                                    className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                >
                                    <button
                                        type="button"
                                        className="inline-flex items-center hover:text-gray-800 dark:hover:text-gray-200"
                                        onClick={() => onHeaderClick(columnId)}
                                    >
                                        {t(`changes_overview.columns.${columnId}`)}
                                        <SortIndicator
                                            active={sortBy === columnId}
                                            dir={sortDir}
                                        />
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-800">
                        {changes.map((change) => {
                            const selected = selectedChangeIds.includes(change._id);
                            return (
                                <tr
                                    key={change._id}
                                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/60 ${selected ? 'bg-indigo-50 dark:bg-indigo-950/30' : ''}`}
                                    onClick={() => onRowClick(change)}
                                    onMouseDown={preventMiddleClickAutoscroll}
                                    onAuxClick={(event) => handleChangeMiddleClick(event, change)}
                                >
                                    <td className="px-3 py-2">
                                        <input
                                            type="checkbox"
                                            checked={selected}
                                            onChange={() => {}}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onToggleSelect(change._id);
                                            }}
                                            className="h-4 w-4 cursor-pointer"
                                        />
                                    </td>
                                    {orderedVisibleColumns.map((columnId) => (
                                        <td
                                            key={columnId}
                                            className={`px-3 py-2 text-gray-700 dark:text-gray-200 ${columnId === 'title' ? 'max-w-md truncate' : 'whitespace-nowrap'}`}
                                        >
                                            {renderCell(change, columnId)}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ChangesOverviewTable;
